import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Switch,
  message,
} from "antd";
import arLocale from "antd/es/date-picker/locale/ar_EG";
import enLocale from "antd/es/date-picker/locale/en_US";
import dayjs from "dayjs";
import { FC } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import {
  adminAddTeamMemberVacation,
  adminGetTimeOffTypes,
} from "../../../network/admin";
import { addTimeOff, getTimeOffTypes } from "../../../network/timeOff";
import { queryKeys } from "../../../services/react-query/queryKeys";
import { TimeOffTypes } from "../../../types/timeOffTypes";

interface AddTimeOffProps {
  visible: boolean;
  onClose: () => void;
  team_member_id: number;
  selectedDate: any;
  location_id?: number;
  config?: {
    isAdmin: boolean;
    isEdit: boolean;
    salonId?: number;
    vacationId?: number;
  };
}

const AddTimeOff: FC<AddTimeOffProps> = ({
  visible,
  onClose,
  team_member_id,
  selectedDate,
  location_id,
  config,
}) => {
  const intl = useIntl();
  const { currentLang } = useSelector(
    (state: { user: { currentLang: "en" | "ar" } }) => state.user
  );
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const approved = Form.useWatch("approved", form);

  const { mutate: onAddTimeOff, isLoading } = useMutation(
    (params) => {
      if (config?.isAdmin) {
        if (config?.isEdit && config?.vacationId) {
        } else {
          return adminAddTeamMemberVacation(
            config?.salonId,
            team_member_id,
            params
          );
        }
      } else {
        if (config?.isEdit && config?.vacationId) {
        } else {
          return addTimeOff(team_member_id, params);
        }
      }
    },
    {
      onSuccess: (res: any) => {
        console.log(res, "res");
        if (res?.code !== "ERR_BAD_REQUEST") {
          message.success(
            intl.formatMessage({
              id: "vacationAdded",
              defaultMessage: "تم إضافة العطلة بنجاح",
            })
          );

          queryClient.invalidateQueries([queryKeys.workingHours]);
          queryClient.invalidateQueries([queryKeys.timeOffs]);
          queryClient.invalidateQueries(["TeamMember"]);
          queryClient.invalidateQueries(["vacation"]);

          onClose();
        } else {
          message.error(res?.response?.data?.message);
        }
      },
      onError: (error) => {
        message.error(
          intl.formatMessage({
            id: "tryAgain",
            defaultMessage: "حاول مرة اخرى",
          })
        );
      },
    }
  );

  const { data: timeOffTypes, isLoading: timeOffTypesLoading } = useQuery(
    [queryKeys.timeOffTypes, config?.salonId],
    () => {
      if (config?.isAdmin) {
        return adminGetTimeOffTypes(config?.salonId);
      } else {
        return getTimeOffTypes();
      }
    }
  );
  const options: any = Array.isArray(timeOffTypes?.data)
    ? timeOffTypes?.data?.map((item: TimeOffTypes) => ({
        value: item.id,
        label: item.reason,
      }))
    : Array.isArray(timeOffTypes?.data?.data)
    ? timeOffTypes?.data?.data?.map((item: TimeOffTypes) => ({
        value: item.id,
        label: item.reason,
      }))
    : [];
  const repeat = Form.useWatch("repeat", form);

  const onFinish = async (values: any) => {
    console.log(values);

    const date = {
      ...values,
      start_date: values["start_date"].format("YYYY-MM-DD"),
      // start_time: values["start_time"].format("HH:mm:ss"),
      // start_time: values["start_date"]?.format("HH:mm:ss"),
      // end_time: values["repeat_until"]?.format("HH:mm:ss"),
      repeat_until: values["repeat_until"]
        ? values["repeat_until"].format("YYYY-MM-DD")
        : null,
      approved: approved,
    };
    onAddTimeOff(date);
  };

  dayjs.locale("ar");
  const dayjsSelectedDate = dayjs(selectedDate, { locale: currentLang });
  const dayName = dayjsSelectedDate.format("dddd");

  const monthName = dayjsSelectedDate.format("MMMM");
  const dayNumber = dayjsSelectedDate.date();

  return (
    <Modal
      centered
      open={visible}
      onCancel={onClose}
      okButtonProps={{ disabled: true }}
      cancelButtonProps={{ disabled: true }}
      footer={[]}
      width={850}
      destroyOnClose={true}
    >
      <Card className="mx-12 my-7 border-0">
        <div className="text-color-primary text-2xl font-semibold leading-7 text-center">
          {intl.formatMessage({
            id: "vacationDay",
            defaultMessage: " يوم العطلة",
          })}
        </div>

        <div className="my-4 text-color-gray text-base font-normal leading-7 text-center">
          {dayName} - {dayNumber} {monthName}
        </div>

        <Form
          name="complex-form"
          onFinish={onFinish}
          layout="vertical"
          className="form-style"
          form={form}
          initialValues={{
            approved: false,
            start_date: dayjsSelectedDate,
          }}
        >
          <Row gutter={{ xs: 1, sm: 1, md: 8, lg: 14, xl: 14 }}>
            <Col xl={12} lg={8} xs={24} sm={24}>
              <Form.Item
                name={"time_off_type_id"}
                label={intl.formatMessage({
                  id: "vacationType",
                  defaultMessage: "طبيعة العطلة",
                })}
                required={false}
                className="form-style w-full"
              >
                <Select
                  loading={timeOffTypesLoading}
                  placeholder={intl.formatMessage({
                    id: "selectVacationType",
                    defaultMessage: "اختر نوع طبيعة العطلة ",
                  })}
                  id="input-with-icon-adornment"
                  options={options}
                />
              </Form.Item>
            </Col>
            <Col
              className="gutter-row"
              xl={12}
              lg={12}
              xs={24}
              sm={24}
              span={11}
            >
              <Form.Item
                name={"start_date"}
                label={intl.formatMessage({
                  id: "vacationDay",
                  defaultMessage: "يوم العطلة",
                })}
                className="w-full"
                required={false}
                rules={[
                  {
                    required: true,
                    message: intl.formatMessage({
                      id: "vacationDayRequired",
                      defaultMessage: "يوم العطلة مطلوب",
                    }),
                  },
                ]}
              >
                <DatePicker
                  locale={currentLang === "ar" ? arLocale : enLocale}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            <Col
              className="gutter-row"
              xl={24}
              lg={12}
              xs={24}
              sm={24}
              span={11}
            >
              <Form.Item name={"repeat"} className="w-full" required={false}>
                <Checkbox
                  onChange={(e) =>
                    form.setFieldsValue({ repeat: e.target.checked })
                  }
                >
                  {intl.formatMessage({
                    id: "vacationRepeat",
                    defaultMessage: "تكرار العطلة",
                  })}
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>
          {repeat && (
            <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
              <Col
                className="gutter-row "
                xl={24}
                lg={12}
                xs={24}
                sm={24}
                span={11}
              >
                <Form.Item
                  name={"repeat_until"}
                  label={intl.formatMessage({
                    id: "vacationEnd",
                    defaultMessage: "نهاية العطلة",
                  })}
                  className="w-full  "
                  required={false}
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({
                        id: "vacationEndRequired",
                        defaultMessage: "نهاية العطلة مطلوب",
                      }),
                    },
                  ]}
                >
                  <DatePicker
                    rootClassName="zoom-0-8"
                    locale={currentLang === "ar" ? arLocale : enLocale}
                    // style={{ zoom: 0.8 }}

                    placement="bottomLeft"
                    disabledDate={(current) => {
                      const selectedStartDate =
                        form.getFieldValue(`start_date`);
                      return current && current < selectedStartDate;
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          <Form.Item
            name={"description"}
            label={intl.formatMessage({
              id: "Description",
              defaultMessage: "وصف",
            })}
            required={false}
            className="w-full"
            rules={[
              // {
              //   required: true,
              //   message: intl.formatMessage({
              //     id: "descriptionRequired",
              //     defaultMessage: "الوصف مطلوب",
              //   }),
              // },
              {
                type: "string",
                min: 6,
                message: intl.formatMessage({
                  id: "descGt5Chars",
                  defaultMessage: "يجب أن يحتوي الاسم على ما لا يقل عن 6 أحرف",
                }),
              },
            ]}
          >
            <Input.TextArea
              size="large"
              rows={2}
              placeholder={intl.formatMessage({
                id: "enterVacationReason",
                defaultMessage: "أدخل وصف سبب العطلة",
              })}
            />
          </Form.Item>

          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            <Col
              className="gutter-row"
              xl={24}
              lg={24}
              xs={24}
              sm={24}
              span={24}
            >
              {/* <Form.Item name="approved" className="text-center">
                <Switch
                  className="custom-switch-text"
                  checkedChildren={
                    <span style={{ color: "#00086D" }}> عطلة معتمدة</span>
                  }
                  unCheckedChildren={
                    <span style={{ color: "gray" }}>عطلة غير معتمدة</span>
                  }
                />
              </Form.Item> */}
              <div className="flex justify-center items-baseline gap-2">
                <span
                  className={
                    !approved ? "text-color-primary" : "text-color-sec-title"
                  }
                >
                  {intl.formatMessage({
                    id: "nonApprovedVacation",
                    defaultMessage: "عطلة غير معتمدة",
                  })}
                </span>

                <Form.Item
                  name={"approved"}
                  valuePropName="checked"
                  colon={false}
                  required={false}
                  labelAlign="left"
                  // label={"عطلة  معتمدة"}
                >
                  <Switch className="mx-2 w-[65px]" />
                </Form.Item>
                <span
                  className={
                    approved ? "text-color-primary" : "text-color-sec-title"
                  }
                >
                  {intl.formatMessage({
                    id: "approvedVacation",
                    defaultMessage: "عطلة  معتمدة",
                  })}
                </span>
              </div>
            </Col>
          </Row>

          <Flex gap="middle" align="center" justify="center">
            <Button type="primary" block htmlType="submit" loading={isLoading}>
              {intl.formatMessage({
                id: "continue",
                defaultMessage: "الاستمرار",
              })}
            </Button>
            <Button
              type="primary"
              block
              className="btn-primary-light"
              onClick={onClose}
            >
              {intl.formatMessage({
                id: "cancel",
                defaultMessage: "إلغاء",
              })}
            </Button>
          </Flex>
        </Form>
      </Card>
    </Modal>
  );
};

export default AddTimeOff;
